/**
 * Вести улья: лента событий за 14 суток (§22.2).
 */

import { formatCrystals, formatDateTime, type LocaleCode } from "@hive/i18n";
import { useTranslation } from "react-i18next";

import type { FeedEventView } from "../api/feed.js";
import { feedEventI18nKey, feedEventParams, type FeedEventParams } from "../game/feed-format.js";
import { Crystals } from "./CrystalIcon.jsx";
import "./feed/feed.css";

export interface FeedScreenProps {
  readonly locale: LocaleCode;
  readonly timeZone: string;
  readonly events: readonly FeedEventView[];
  readonly unreadCount: number;
  readonly loading?: boolean;
  readonly onClose: () => void;
  readonly onMarkRead: () => void;
  readonly onOpenHex?: (hexId: string) => void;
}

export function FeedScreen({
  locale,
  timeZone,
  events,
  unreadCount,
  loading = false,
  onClose,
  onMarkRead,
  onOpenHex,
}: FeedScreenProps): React.JSX.Element {
  const { t } = useTranslation(["feed", "common", "economy"]);

  const tierLabel = (tier: string): string =>
    t(`economy:debt.tier.${tier}`, { defaultValue: tier });

  const formatAmountParam = (value: number): string =>
    formatCrystals(locale, value * 1000, "hive");

  return (
    <section className="feed-screen" aria-label={t("feed:screen.title")}>
      <header className="feed-screen__head">
        <div>
          <h2 className="feed-screen__title">{t("feed:screen.title")}</h2>
          {unreadCount > 0 && (
            <p className="feed-screen__unread">{t("feed:screen.unread", { count: unreadCount })}</p>
          )}
        </div>
        <button type="button" className="button" onClick={onClose}>
          {t("common:action.close")}
        </button>
      </header>

      {loading ? (
        <p className="feed-screen__state">{t("common:state.loading")}</p>
      ) : events.length === 0 ? (
        <p className="feed-screen__state">{t("feed:screen.empty")}</p>
      ) : (
        <ul className="feed-screen__list">
          {events.map((event) => {
            const rawParams = feedEventParams(event, tierLabel);
            const params: FeedEventParams = { ...rawParams };
            if (typeof params["amount"] === "number") {
              params["amount"] = formatAmountParam(params["amount"]);
            }
            if (typeof params["stake"] === "number") {
              params["stake"] = formatAmountParam(params["stake"]);
            }
            if (typeof params["award"] === "number") {
              params["award"] = formatAmountParam(params["award"]);
            }
            if (typeof params["debt"] === "number") {
              params["debt"] = formatAmountParam(params["debt"]);
            }

            const i18nParams: Record<string, string | number> = {};
            for (const [key, value] of Object.entries(params)) {
              if (value !== undefined) {
                i18nParams[key] = value;
              }
            }

            return (
              <li key={event.id} className={event.read ? "feed-item" : "feed-item feed-item--new"}>
                <time className="feed-item__time" dateTime={event.createdAt}>
                  {formatDateTime(locale, new Date(event.createdAt), timeZone)}
                </time>
                <p className="feed-item__text">
                  <Crystals>{t(`feed:${feedEventI18nKey(event.kind)}`, i18nParams)}</Crystals>
                </p>
                {event.hexId && onOpenHex && (
                  <button
                    type="button"
                    className="button button--link feed-item__hex"
                    onClick={() => onOpenHex(event.hexId as string)}
                  >
                    {t("feed:action.open_hex")}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {unreadCount > 0 && (
        <footer className="feed-screen__foot">
          <button type="button" className="button button--primary" onClick={onMarkRead}>
            {t("feed:action.mark_read")}
          </button>
        </footer>
      )}
    </section>
  );
}
